// Shared dnd-kit kinds + droppable-id factory.
//
// Every draggable and droppable in the QueryBuilder canvas declares a `kind`
// on its `data` so the central `handleDragEnd` (see useQueryBuilderDnd.js)
// can decide what mutation to fire based on the (active.kind, over.kind)
// pair. Treat the strings here as the public contract between targets and
// the central handler.

// ── Draggable kinds ──────────────────────────────────────────────────────────

/** An entity dragged in from the catalog sidebar (data: { entity }). */
export const DRAG_ENTITY_SOURCE = "entity-source";

/** A field dragged out of an entity accordion (data: { entityField, displayedEntityId }). */
export const DRAG_ENTITY_FIELD = "entity-field";

export const DRAG_DATA_FIELD = "data-field";

export const DRAG_ORDER_BY = "order-by-drag";

export const DRAG_GROUP_BY = "group-by-drag";

/** A field dragged out of a condition's left/right side (data: { entityField, fieldsData, side, queryId, groupId, forJoin }). */
export const DRAG_CONDITION_FIELD = "condition-field-drag";

// ── Droppable kinds ──────────────────────────────────────────────────────────

export const DROP_ENTITIES_TARGET = "entities-target";       // attachEntity
export const DROP_DATA_FIELDS_TARGET = "data-fields-target"; // postDataField
export const DROP_GROUP_BY_TARGET = "group-by-target";       // postGroupBy
export const DROP_ORDER_BY_TARGET = "order-by-target";       // postOrderBy
export const DROP_ORDER_BY_SLOT = "order-by-slot";           // reorderOrderBy

/** A condition group as a whole — drop here to add a new condition row. */
export const DROP_CONDITION_GROUP = "condition-group";

/** The operator-select cell of an existing condition — drop here to nest a new group. */
export const DROP_CONDITION_SELECT = "condition-select";

/** The left or right field of an existing condition — drop here to swap that side. */
export const DROP_CONDITION_LEFT_FIELD = "condition-left-field";
export const DROP_CONDITION_RIGHT_FIELD = "condition-right-field";

// ── Droppable id factories ───────────────────────────────────────────────────
// `useDroppable.id` must be globally unique; we encode context into it but
// rely on `data.current` for decisions in `handleDragEnd`.

export const entitiesTargetId = (queryId) =>
  `${DROP_ENTITIES_TARGET}::${queryId}`;

export const dataFieldsTargetId = (queryId) =>
  `${DROP_DATA_FIELDS_TARGET}::${queryId}`;

/**
 * Data Field chips share a single id for both their draggable and droppable
 * roles (the "slot" they can be reordered onto), instead of the separate
 * drag/drop id pairs used elsewhere in this file.
 */
export const dataFieldChipId = (dataFieldId) =>
  `${DRAG_DATA_FIELD}::${dataFieldId}`;

export const groupByTargetId = (queryId) =>
  `${DROP_GROUP_BY_TARGET}::${queryId}`;

export const orderByTargetId = (queryId) =>
  `${DROP_ORDER_BY_TARGET}::${queryId}`;

export const orderBySlotId = (orderById) =>
  `${DROP_ORDER_BY_SLOT}::${orderById}`;

export const conditionGroupId = ({ queryId, groupId, forJoin }) =>
  `${DROP_CONDITION_GROUP}::${queryId}::${groupId ?? "top"}::${forJoin ? "join" : "cond"}`;

export const conditionSelectId = ({ queryId, groupId, fieldId }) =>
  `${DROP_CONDITION_SELECT}::${queryId}::${groupId}::${fieldId}`;

export const conditionLeftFieldId = ({ queryId, groupId, fieldId }) =>
  `${DROP_CONDITION_LEFT_FIELD}::${queryId}::${groupId}::${fieldId}`;

export const conditionRightFieldId = ({ queryId, groupId, fieldId }) =>
  `${DROP_CONDITION_RIGHT_FIELD}::${queryId}::${groupId}::${fieldId}`;
